import { useRef, useCallback } from 'react';

export default function useDragTranslate(onFocus) {
  const elRef = useRef(null);
  const draggingRef = useRef(false);

  // starting mouse position when drag begins
  const startMouse = useRef({ x: 0, y: 0 });
  // starting tx/ty (in px) when drag begins
  const startDelta = useRef({ tx: 0, ty: 0 });
  /* this hook handles dragging windows around using css variables */

  const getCurrentDelta = () => {
    const el = elRef.current;
    if (!el) return { tx: 0, ty: 0 };
    const cs = getComputedStyle(el);
    const tx = parseFloat(cs.getPropertyValue('--tx')) || 0;
    const ty = parseFloat(cs.getPropertyValue('--ty')) || 0;
    return { tx, ty };
    /* gets the current css variable values for position */
  };

  const setDelta = (tx, ty) => {
    const el = elRef.current;
    if (!el) return;
    el.style.setProperty('--tx', `${tx}px`);
    el.style.setProperty('--ty', `${ty}px`);
    /* updates the css variables to move the window */
  };

  const onPointerDown = useCallback((e) => {
    // ignore clicks on controls
    if (e.target.closest('.title-bar-controls')) return;

    const el = elRef.current;
    if (!el) return;

    // bring window to front immediately when drag starts
    onFocus && onFocus();

    // capture pointer so drag keeps receiving events
    el.setPointerCapture?.(e.pointerId);

    draggingRef.current = true;
    el.classList.add('dragging');
    /* start the drag operation */

    const { tx, ty } = getCurrentDelta();
    startDelta.current = { tx, ty };
    startMouse.current = { x: e.clientX, y: e.clientY };
  }, [onFocus]);

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startMouse.current.x;
    const dy = e.clientY - startMouse.current.y;
    const tx = startDelta.current.tx + dx;
    const ty = startDelta.current.ty + dy;
    setDelta(tx, ty);
    /* update position as mouse moves */
  }, []);

  const onPointerUp = useCallback((e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    elRef.current?.classList.remove('dragging');
    elRef.current?.classList.add('dragged'); // mark as having been dragged
    elRef.current?.releasePointerCapture?.(e.pointerId);
    /* stop dragging and clean up */
  }, []);

  return {
    elRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
